import { useCallback, useMemo, useState } from "react";
import type { CurrencyModel } from "../viewModels/Settings/CurrencyModel";
import formatPrice from "../utils/formatPrice";
import calculateSummaryCurrencyValues, { SummaryCurrencyRow, SummaryCurrencyValues } from "../utils/getSummaryCurrencyValues";

type SummaryCurrencySettings = {
  currencies: Array<CurrencyModel>;
  baseCurrency: CurrencyModel;
  scopeKey: string;
};

export default function useSummaryCurrency({ currencies, baseCurrency, scopeKey }: SummaryCurrencySettings) {
  const [selection, setSelection] = useState<{ scopeKey: string; baseCurrencyId: number; currencyId: number }>();
  const availableCurrencies = useMemo(
    () => [baseCurrency, ...currencies.filter((currency) => currency.id !== baseCurrency.id)],
    [baseCurrency, currencies],
  );
  const selectedCurrency =
    selection?.scopeKey === scopeKey && selection.baseCurrencyId === baseCurrency.id
      ? availableCurrencies.find((currency) => currency.id === selection.currencyId)
      : undefined;
  const currency = selectedCurrency ?? baseCurrency;

  const options = useMemo(
    () =>
      availableCurrencies.map((currency) => ({
        value: currency.id,
        label: `${currency.symbol} ${currency.name}`,
        disabled: false,
      })),
    [availableCurrencies],
  );
  const onChange = useCallback(
    (currencyId: number) => setSelection({ scopeKey, baseCurrencyId: baseCurrency.id, currencyId }),
    [baseCurrency.id, scopeKey],
  );
  const formatSummaryPrice = useCallback(
    (amount: number | undefined) => (amount === undefined || !Number.isFinite(amount) ? "—" : formatPrice(amount, currency.symbol)),
    [currency.symbol],
  );
  const getSummaryValues = useCallback(
    (rows: Array<SummaryCurrencyRow>, baseValues: SummaryCurrencyValues) =>
      currency.id === baseCurrency.id
        ? baseValues
        : calculateSummaryCurrencyValues(rows, availableCurrencies, baseCurrency.id, currency.id),
    [availableCurrencies, baseCurrency.id, currency.id],
  );

  return { currency, currencySelectProps: { value: currency.id, options, onChange }, formatSummaryPrice, getSummaryValues };
}
